/*
 * Broadcast Theme Custom JS
 */

//
// ---------- Helpers ----------
//
function myLog(...args) {
  const prefix = "%c[GIFT PRODUCTS]";
  const style = "color:rgb(230,84,17);font-weight:bold;font-size:16px;";
  console.log(prefix, style, ...args);
}

const usdPrice = (price) => {
  const { currency } = window.Shopify;
  if (typeof price === "string") price = price.replace(/,/g, "");
  const parsed = parseFloat(price);
  if (isNaN(parsed)) return 0;
  return currency.active === "USD" ? parsed : parsed / currency.rate;
};

//
// ---------- <pf-gift-products> ----------
//
class PfGiftProducts extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    this.renderGiftList();
    this.quantityEl?.addEventListener("change", this.renderGiftList.bind(this), { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  // --- Data Getters ---
  get giftProducts() { return JSON.parse(this.dataset.giftProducts || "[]"); }
  get cartData() { return JSON.parse(this.dataset.cartData || "{}"); }
  get quantityEl() { return document.querySelector(".quantity__input[name='quantity']"); }
  get productPrice() { return this.dataset.productPrice || 0; }
  get giftCards() { return this.querySelectorAll(".pf-gift-item"); }
  get productForm() { return this.querySelector("product-form form[action='/cart/add']"); }

  get totalPrice() {
    const quantity = Number(this.quantityEl?.value) || 0;
    const productPrice = usdPrice(this.productPrice) || 0;
    const cartTotal = usdPrice((this.cartData?.total_price || 0) / 100);
    return cartTotal + productPrice * quantity;
  }

  // --- Threshold Logic ---
  getThresholds() {
    const thresholds = this.giftProducts.map(g => g.minimum_order_amount).sort((a, b) => a - b);
    let active = null, next = null;

    for (let i = 0; i < thresholds.length; i++) {
      const min = thresholds[i];
      if (this.totalPrice >= min) {
        active = min;
        next = thresholds[i + 1] || null;
      } else {
        next = min;
        break;
      }
    }

    return { activeThreshold: active, nextThreshold: next, topThreshold: thresholds.at(-1) };
  }

  getGiftOnCart() {
    const item = this.cartData.items?.find(item =>
      this.querySelector(`.pf-gift-item[data-product-id="${item.product_id}"]`)
    );
    return item?.variant_id || null;
  }

  // --- Rendering ---
  renderGiftList() {
    const { activeThreshold, nextThreshold, topThreshold } = this.getThresholds();
    const giftOnCart = this.getGiftOnCart();
    const footer = this.querySelector(".pf-gift-footer p");

    let selected = false;
    this.giftCards.forEach(card => {
      const minAmount = Number(card.dataset.minAmount);
      const show = minAmount === activeThreshold;
      card.classList.toggle("pf-show", show || minAmount === nextThreshold);

      const shouldSelect = show && !selected;
      card.classList.toggle("pf-selected", shouldSelect);
      if (shouldSelect) selected = true;
    });

    // --- Message Logic ---
    let message = "";
    if (
      !nextThreshold &&
      giftOnCart &&
      giftOnCart == document.querySelector(`.pf-gift-item[data-min-amount='${topThreshold}']`)?.dataset.variantId
    ) {
      message = this.dataset.giftCartMessage;
    } else if (!activeThreshold && nextThreshold) {
      const diff = Math.max(0, nextThreshold - this.totalPrice);
      message = this.dataset.firstMessage?.replace("[amount]", `$${diff.toFixed(2)}`);
    } else if (activeThreshold && nextThreshold) {
      const diff = Math.max(0, nextThreshold - this.totalPrice);
      message = this.dataset.nextMessage?.replace("[amount]", `$${diff.toFixed(2)}`);
    } else if (activeThreshold && !nextThreshold) {
      message = this.dataset.allGiftsMessage;
    }

    if (footer) footer.textContent = message;
    this.removeAttribute("data-hidden");
  }
}

if (!customElements.get("pf-gift-products")) {
  customElements.define("pf-gift-products", PfGiftProducts);
}

//
// ---------- <pf-global-gift> ----------
//
class PfGlobalGift extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    document.addEventListener("theme:product:added", this.updateCartGift.bind(this), { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  // --- Getters ---
  get giftProducts() {
    return JSON.parse(this.dataset.giftProducts);
  }

  // --- Threshold Logic ---
  getThresholds(totalPrice) {
    const thresholds = this.giftProducts.map(g => g.minimum_order_amount).sort((a, b) => a - b);
    let active = null, next = null;

    for (let i = 0; i < thresholds.length; i++) {
      const min = thresholds[i];
      if (totalPrice >= min) {
        active = min;
        next = thresholds[i + 1] || null;
      } else {
        next = min;
        break;
      }
    }

    return { activeThreshold: active, nextThreshold: next };
  }

  // --- Cart Gift Update ---
  async updateCartGift() {
    const checkoutBtn = document.querySelector("button[type='submit'][name='checkout']");
    setTimeout(() => {
      checkoutBtn?.setAttribute("disabled", true);
    }, 2000);

    const cartWrapper = document.querySelector("#cart-drawer");
    cartWrapper?.classList.add("checkout-disabled");

    const res = await fetch("/cart.js");
    const data = await res.json();

    const giftOnCart = data.items?.find(item =>
      this.querySelector(`[data-gift-item][data-product-id="${item.product_id}"]`)
    );

    const { activeThreshold } = this.getThresholds(usdPrice(data.total_price / 100));
    const productVariant = this.querySelector(`[data-gift-item][data-min-amount="${activeThreshold}"]`)?.dataset.variantId;

    if (activeThreshold && !giftOnCart) {
      await this.modifyCart("add", productVariant);
    } else if (activeThreshold && giftOnCart && giftOnCart.variant_id != productVariant) {
      await this.modifyCart("remove", giftOnCart.variant_id);
      await this.modifyCart("add", productVariant);
    } else if (!activeThreshold && giftOnCart) {
      await this.modifyCart("remove", giftOnCart.variant_id);
    }

    document.querySelector("cart-items").getCart(false);

    cartWrapper?.classList.remove("checkout-disabled");
    setTimeout(() => {
      checkoutBtn?.removeAttribute("disabled");
    }, 1500);
  }

  async modifyCart(type, variantId) {
    try {
      const sectionId = document.querySelector("pf-gift-products")?.dataset?.sectionId;
      const section = sectionId ? `?sections=${sectionId}` : "";
      let res;

      if (type === "add") {
        res = await fetch(`/cart/add.js${section}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: Number(variantId), quantity: 1, properties: { _free_gift: true } })
        });
      } else if (type === "remove") {
        res = await fetch(`/cart/update.js${section}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updates: { [variantId]: 0 } })
        });
      }

      if (!res.ok) throw new Error(`${type} request failed`);
      const json = await res.json();
      this.updateProductPage(json?.sections?.[sectionId]);
      return json;
    } catch (err) {
      console.error(`❌ modifyCart(${type}) error:`, err);
      return null;
    }
  }

  updateProductPage(sectionHtml) {
    if (!sectionHtml) return;
    const html = document.createElement("div");
    html.innerHTML = sectionHtml;

    const newGiftProducts = html.querySelector("pf-gift-products");
    const oldGiftProducts = document.querySelector("pf-gift-products");
    if (newGiftProducts && oldGiftProducts) {
      oldGiftProducts.outerHTML = newGiftProducts.outerHTML;
    }
  }
}

if (!customElements.get("pf-global-gift")) {
  customElements.define("pf-global-gift", PfGlobalGift);
}
